import React, { useState, useRef } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import styles from './Animation.module.css';

const Animation = () => {
  const [state, handleSubmit] = useForm("xblypjlp");
  const [videoStates, setVideoStates] = useState({
    mcgill: { muted: false, playing: false },
    fitmenu: { muted: false, playing: false },
    ad570m: { muted: false, playing: false }
  });
  
  const videoRefs = {
    mcgill: useRef(null),
    fitmenu: useRef(null),
    ad570m: useRef(null)
  };

  const handleVideoHover = (videoKey, isHovering) => {
    const video = videoRefs[videoKey].current;
    if (video) {
      if (isHovering) {
        video.play();
        setVideoStates(prev => ({
          ...prev,
          [videoKey]: { ...prev[videoKey], playing: true }
        }));
      } else {
        video.pause();
        setVideoStates(prev => ({
          ...prev,
          [videoKey]: { ...prev[videoKey], playing: false }
        }));
      }
    }
  };

  const handleVideoClick = (videoKey) => {
    const video = videoRefs[videoKey].current;
    const currentState = videoStates[videoKey];
    
    if (video) {
      if (currentState.playing) {
        video.pause();
        setVideoStates(prev => ({
          ...prev,
          [videoKey]: { ...prev[videoKey], playing: false }
        }));
      } else {
        video.play();
        setVideoStates(prev => ({
          ...prev,
          [videoKey]: { ...prev[videoKey], playing: true }
        }));
      }
    }
  };

  const toggleMute = (videoKey) => {
    const video = videoRefs[videoKey].current;
    const currentState = videoStates[videoKey];
    
    if (video) {
      video.muted = !currentState.muted;
      setVideoStates(prev => ({
        ...prev,
        [videoKey]: { ...prev[videoKey], muted: !currentState.muted }
      }));
    }
  };

  return (
    <div className={styles.animationPage}>


      {/* 3D Modeling Section */}
      <section className={styles.modelingSection}>
        <div className={styles.container}>
          {/* Hero Image for 3D Modeling */}
          <div className={styles.heroImage}>
            <img 
              src="/images/3d-modeling-hero.png" 
              alt="3D Modeling Showcase" 
              className={styles.heroPhoto}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className={styles.imagePlaceholder} style={{display: 'none'}}>
              <span className={styles.placeholderText}>3D Modeling Hero Image</span>
              <span className={styles.placeholderNote}>Large showcase image of 3D models/objects</span>
            </div>
          </div>
          
          <p className={styles.sectionDescription}>
            Offers precise and creative 3D modeling services for a wide range of projects. From characters to props,
            environments, and digital products, models are crafted with attention to detail and optimized for
            animation, rendering, or real-time applications.
          </p>
          
          {/* 3 Remote Photos in a Row */}
          <div className={styles.remoteGallery}>
            <div className={styles.remoteItem}>
              <img 
                src="/images/remote-1.png" 
                alt="Remote Control 1" 
                className={styles.remotePhoto}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={styles.imagePlaceholder} style={{display: 'none'}}>
                <span className={styles.placeholderText}>Remote 1</span>
                <span className={styles.placeholderNote}>First remote control</span>
              </div>
            </div>
            <div className={styles.remoteItem}>
              <img 
                src="/images/remote-2.png" 
                alt="Remote Control 2" 
                className={styles.remotePhoto}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={styles.imagePlaceholder} style={{display: 'none'}}>
                <span className={styles.placeholderText}>Remote 2</span>
                <span className={styles.placeholderNote}>Second remote control</span>
              </div>
            </div>
            <div className={styles.remoteItem}>
              <img 
                src="/images/remote-3.png" 
                alt="Remote Control 3" 
                className={styles.remotePhoto}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={styles.imagePlaceholder} style={{display: 'none'}}>
                <span className={styles.placeholderText}>Remote 3</span>
                <span className={styles.placeholderNote}>Third remote control</span>
              </div>
            </div>
          </div>
          
          {/* 2 Bird Photos Side by Side */}
          <div className={styles.birdGallery}>
            <div className={styles.birdItem}>
              <img 
                src="/images/bird-1.png" 
                alt="Bird 1" 
                className={styles.birdPhoto}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={styles.imagePlaceholder} style={{display: 'none'}}>
                <span className={styles.placeholderText}>Bird 1</span>
                <span className={styles.placeholderNote}>First bird image</span>
              </div>
            </div>
            <div className={styles.birdItem}>
              <img 
                src="/images/bird-2.png" 
                alt="Bird 2" 
                className={styles.birdPhoto}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={styles.imagePlaceholder} style={{display: 'none'}}>
                <span className={styles.placeholderText}>Bird 2</span>
                <span className={styles.placeholderNote}>Second bird image</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animation Section */}
      <section className={styles.animationSection}>
        <div className={styles.container}>
          {/* Hero Image for Animation */}
          <div className={styles.animationHeroImage}>
            <img 
              src="/images/animation-hero.png" 
              alt="Animation Showcase" 
              className={styles.animationHeroPhoto}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className={styles.imagePlaceholder} style={{display: 'none'}}>
              <span className={styles.placeholderText}>Animation Hero Image</span>
              <span className={styles.placeholderNote}>Large showcase image for animation section</span>
            </div>
          </div>
          
          <p className={styles.sectionDescription}>
            Delivers dynamic and expressive 3D animations that bring digital creations to life. From product
            showcases to character performances and animated sequences, animations are tailored to enhance
            storytelling, marketing, and branded content.
          </p>
          
          <div className={styles.videoGrid}>
            <div className={styles.videoContainer}>
              <div className={`${styles.videoItem} ${styles.verticalVideo}`}>
                <video
                  ref={videoRefs.mcgill}
                  src="/videos/mcgill-commercial.mp4"
                  className={styles.videoPlayer}
                  muted={videoStates.mcgill.muted}
                  loop
                  playsInline
                  onMouseEnter={() => handleVideoHover('mcgill', true)}
                  onMouseLeave={() => handleVideoHover('mcgill', false)}
                  onClick={() => handleVideoClick('mcgill')}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className={styles.videoPlaceholder} style={{display: 'none'}}>
                  <span className={styles.playIcon}>▶</span>
                  <span className={styles.videoTitle}>McGill playing their cards commercial.mp4</span>
                </div>
                {!videoStates.mcgill.playing && (
                  <div className={styles.videoOverlay}>
                    <span className={styles.hoverText}>Hover to play</span>
                  </div>
                )}
              </div>
              <div className={styles.videoControls}>
                <button 
                  className={styles.muteButton}
                  onClick={() => toggleMute('mcgill')}
                >
                  <svg className={styles.speakerIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19.07 4.93A10 10 0 0 1 19.07 19.07M15.54 8.46A5 5 0 0 1 15.54 15.54" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    {videoStates.mcgill.muted && (
                      <path d="M23 9L9 23" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    )}
                  </svg>
                </button>
                <span className={styles.videoLabel}>McGill Commercial</span>
              </div>
            </div>
            
            <div className={styles.videoContainer}>
              <div className={`${styles.videoItem} ${styles.verticalVideo}`}>
                <video
                  ref={videoRefs.fitmenu}
                  src="/videos/fitmenu-ad.mp4"
                  className={styles.videoPlayer}
                  muted={videoStates.fitmenu.muted}
                  loop
                  playsInline
                  onMouseEnter={() => handleVideoHover('fitmenu', true)}
                  onMouseLeave={() => handleVideoHover('fitmenu', false)}
                  onClick={() => handleVideoClick('fitmenu')}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className={styles.videoPlaceholder} style={{display: 'none'}}>
                  <span className={styles.playIcon}>▶</span>
                  <span className={styles.videoTitle}>FitMenu ad.mp4</span>
                </div>
                {!videoStates.fitmenu.playing && (
                  <div className={styles.videoOverlay}>
                    <span className={styles.hoverText}>Hover to play</span>
                  </div>
                )}
              </div>
              <div className={styles.videoControls}>
                <button 
                  className={styles.muteButton}
                  onClick={() => toggleMute('fitmenu')}
                >
                  <svg className={styles.speakerIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19.07 4.93A10 10 0 0 1 19.07 19.07M15.54 8.46A5 5 0 0 1 15.54 15.54" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    {videoStates.fitmenu.muted && (
                      <path d="M23 9L9 23" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    )}
                  </svg>
                </button>
                <span className={styles.videoLabel}>FitMenu Ad</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Rendering Section */}
      <section className={styles.renderingSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>VISUAL STORYTELLING</h2>
          <h3 className={styles.subsectionTitle}>PRODUCT RENDERING</h3>
          <p className={styles.sectionDescription}>
            Provides high-quality product rendering services, producing photorealistic visuals and cinematic shots
            for advertising, e-commerce, and promotional campaigns. Can include dynamic camera animations,
            soundtracks, and narrative-driven presentations to elevate product experiences.
          </p>
          
          {/* 570M Video Section */}
          <div className={styles.videoContainer}>
            <div className={styles.videoItem}>
              <video
                ref={videoRefs.ad570m}
                src="/videos/570m-ad.mp4"
                className={styles.videoPlayer}
                muted={videoStates.ad570m.muted}
                loop
                playsInline
                onMouseEnter={() => handleVideoHover('ad570m', true)}
                onMouseLeave={() => handleVideoHover('ad570m', false)}
                onClick={() => handleVideoClick('ad570m')}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={styles.videoPlaceholder} style={{display: 'none'}}>
                <span className={styles.playIcon}>▶</span>
                <span className={styles.videoTitle}>570M ad.mp4</span>
              </div>
              {!videoStates.ad570m.playing && (
                <div className={styles.videoOverlay}>
                  <span className={styles.hoverText}>Hover to play</span>
                </div>
              )}
            </div>
            <div className={styles.videoControls}>
              <button 
                className={styles.muteButton}
                onClick={() => toggleMute('ad570m')}
              >
                <svg className={styles.speakerIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19.07 4.93A10 10 0 0 1 19.07 19.07M15.54 8.46A5 5 0 0 1 15.54 15.54" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  {videoStates.ad570m.muted && (
                    <path d="M23 9L9 23" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  )}
                </svg>
              </button>
              <span className={styles.videoLabel}>570M Ad</span>
            </div>
          </div>

          {/* 4 Product Render Photos */}
          <div className={styles.productGrid}>
            <div className={styles.productItem}>
              <img 
                src="/images/product-render-1.png" 
                alt="Product Render 1" 
                className={styles.productPhoto}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={styles.imagePlaceholder} style={{display: 'none'}}>
                <span className={styles.placeholderText}>Product Render 1</span>
                <span className={styles.placeholderNote}>Photorealistic product shot</span>
              </div>
            </div>
            <div className={styles.productItem}>
              <img 
                src="/images/product-render-2.png" 
                alt="Product Render 2" 
                className={styles.productPhoto}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={styles.imagePlaceholder} style={{display: 'none'}}>
                <span className={styles.placeholderText}>Product Render 2</span>
                <span className={styles.placeholderNote}>Cinematic product showcase</span>
              </div>
            </div>
            <div className={styles.productItem}>
              <img 
                src="/images/product-render-3.png" 
                alt="Product Render 3" 
                className={styles.productPhoto}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={styles.imagePlaceholder} style={{display: 'none'}}>
                <span className={styles.placeholderText}>Product Render 3</span>
                <span className={styles.placeholderNote}>E-commerce product visual</span>
              </div>
            </div>
            <div className={styles.productItem}>
              <img 
                src="/images/product-render-4.png" 
                alt="Product Render 4" 
                className={styles.productPhoto}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={styles.imagePlaceholder} style={{display: 'none'}}>
                <span className={styles.placeholderText}>Product Render 4</span>
                <span className={styles.placeholderNote}>Product visualization</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className={styles.contactSection}>
        <div className={styles.container}>
          <div className={styles.heading}>
            <h2 className={styles.contactTitle}>CONTACT US</h2>
            <p className={styles.contactDescription}>
              Ready to bring your vision to life? Get in touch to discuss your project and see how we can help create stunning animations, 3D models, and product renders.
            </p>
          </div>
          
          <div className={styles.contactForm}>
            <div className={styles.formCard}>
              <h3 className={styles.formTitle}>Send us a message</h3>
              
              {state.succeeded ? (
                <div className={styles.successMessage}>
                  <div className={styles.successIcon}>✓</div>
                  <h3>Thanks for your message!</h3>
                  <p>We appreciate you contacting us and will respond shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className={styles.formContent}>
                  <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                      <label htmlFor="name">Your Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        placeholder="John Doe"
                        required
                      />
                      <ValidationError 
                        prefix="Name" 
                        field="name"
                        errors={state.errors}
                      />
                    </div>
                    
                    <div className={styles.formGroup}>
                      <label htmlFor="email">Your Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="john@example.com"
                        required
                      />
                      <ValidationError 
                        prefix="Email" 
                        field="email"
                        errors={state.errors}
                      />
                    </div>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="subject">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      placeholder="Animation project inquiry"
                    />
                    <ValidationError 
                      prefix="Subject" 
                      field="subject"
                      errors={state.errors}
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      placeholder="Tell us about your project..."
                      required
                    ></textarea>
                    <ValidationError 
                      prefix="Message" 
                      field="message"
                      errors={state.errors}
                    />
                  </div>
                  
                  <button type="submit" className={styles.submitButton} disabled={state.submitting}>
                    Send Message
                    <span className={styles.buttonIcon}>→</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Animation; 